import { getWrestlerName } from './wrestlerUtils';
// ─────────────────────────────────────────────────────────────────────────────
// storylinesDatabase.js
// Storyline presets for the Rivalry Storytelling System
//
// VARIABLES:
//   Solo     → {A} = superstar1.name, {B} = superstar2.name, {title} = optional
//   Tag      → {A1} {A2} {B1} {B2} = members, {teamA} {teamB} = team names (or fallback to "{A1} & {A2}")
//   Mixed    → same as solo but gender constraint: one male, one female
//
// PHASES:
//   level 1 = Setup   → rivalry starts
//   level 2 = Build   → tension rises
//   level 3 = Heat    → full escalation
//   level 4 = Climax  → final confrontation (tied to last match before rivalry ends)
//
// TEXT GENERATION:
//   resolveText(template, vars) replaces {X} tokens with actual names
//   pickVariant(phase, usedIndexes) picks randomly, avoids repeats
// ─────────────────────────────────────────────────────────────────────────────

// ── Helper: resolve {tokens} in a template string ──────────────────────────
export function resolveText(template, vars) {
  return template.replace(/\{(\w+)\}/g, function(_, key) {
    var val = vars[key];
    // If the value is a non-empty string, use it
    // If null/undefined/empty, leave the token as-is only when debugging - but for display, return empty string
    return (val != null && val !== '') ? val : '';
  });
}

// ── Helper: pick a random variant, avoid last used ─────────────────────────
export function pickVariant(variants, lastIndex) {
  if (!variants || variants.length === 0) return '';
  var available = variants
    .map(function(v, i) { return { v, i }; })
    .filter(function(x) { return x.i !== lastIndex; });
  var picked = available[Math.floor(Math.random() * available.length)];
  return { text: picked.v, index: picked.i };
}

// ── Helper: build vars object from rivalry + roster ─────────────────────────
export function buildVars(rivalry, roster, champions) {
  // Find wrestler by id (supports number or string IDs)
  var findById = function(id) {
    if (id == null) return null;
    var w = (roster || []).find(function(w) {
      return String(w.wrestler_id) === String(id);
    });
    return w || null;
  };

  // Resolve title name from titleKey
  var resolveTitleName = function(titleKey) {
    if (!titleKey) return null;
    // Look in champions slots
    var slots = (champions && champions.slots) || [];
    var slot = slots.find(function(s) { return s.key === titleKey; });
    if (slot && slot.titleOverride && slot.titleOverride.name) return slot.titleOverride.name;
    if (slot && slot.label) return slot.label;
    // Fallback: prettify the key
    return titleKey.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  };

  // Detect if it's a tag team rivalry (2v2)
  var s1 = rivalry.side1 || [];
  var s2 = rivalry.side2 || [];
  var isTag = rivalry.type === 'team' || s1.length >= 2 || s2.length >= 2;

  // Auto-detect champion among participants - champion is always A, challenger is always B
  var isChampion = function(id) {
    if (!champions || !id) return false;
    var champData = champions.champions || {};
    return Object.values(champData).some(function(holders) {
      return (holders || []).some(function(h) { return h && String(h.wrestler_id) === String(id); });
    });
  };

  if (isTag) {
    var w_s1_0 = findById(s1[0]);
    var w_s1_1 = findById(s1[1]);
    var w_s2_0 = findById(s2[0]);
    var w_s2_1 = findById(s2[1]);
    var side1IsChamp = isChampion(s1[0]) || isChampion(s1[1]);
    var side2IsChamp = isChampion(s2[0]) || isChampion(s2[1]);
    // If side2 is champion and side1 is not: swap A=champion, B=challenger
    var tA1, tA2, tB1, tB2;
    if (side2IsChamp && !side1IsChamp) {
      tA1 = w_s2_0; tA2 = w_s2_1; tB1 = w_s1_0; tB2 = w_s1_1;
    } else {
      tA1 = w_s1_0; tA2 = w_s1_1; tB1 = w_s2_0; tB2 = w_s2_1;
    }
    var A1 = tA1 ? getWrestlerName(tA1, 'short') : '?';
    var A2 = tA2 ? getWrestlerName(tA2, 'short') : null;
    var B1 = tB1 ? getWrestlerName(tB1, 'short') : '?';
    var B2 = tB2 ? getWrestlerName(tB2, 'short') : null;
    return {
      A1: A1, A2: A2, B1: B1, B2: B2,
      teamA: (side2IsChamp && !side1IsChamp ? rivalry.teamName2 : rivalry.teamName1) || (A2 ? A1 + ' & ' + A2 : A1),
      teamB: (side2IsChamp && !side1IsChamp ? rivalry.teamName1 : rivalry.teamName2) || (B2 ? B1 + ' & ' + B2 : B1),
      title: resolveTitleName(rivalry.titleKey || rivalry.title) || rivalry.title || null,
    };
  }

  // SOLO / MIXED - handle both shapes:
  // - { side1: [id], side2: [id] } (from ShowResults pendingCreates)
  // - { superstar1: {name}, superstar2: {name} } (legacy)
  var nameA = '?';
  var nameB = '?';
  var idS1 = rivalry.superstar1 ? rivalry.superstar1.wrestler_id : s1[0];
  var idS2 = rivalry.superstar2 ? rivalry.superstar2.wrestler_id : s2[0];
  var wS1 = rivalry.superstar1 || findById(idS1);
  var wS2 = rivalry.superstar2 || findById(idS2);
  var name1 = wS1 ? (wS1.name || getWrestlerName(wS1, 'short')) : '?';
  var name2 = wS2 ? (wS2.name || getWrestlerName(wS2, 'short')) : '?';

  // If one is a champion: A = champion, B = challenger
  // Otherwise keep side1 = A, side2 = B
  var s1IsChamp = isChampion(idS1);
  var s2IsChamp = isChampion(idS2);
  if (s2IsChamp && !s1IsChamp) {
    // Side 2 is the champion - swap so A = champion
    nameA = name2;
    nameB = name1;
  } else {
    nameA = name1;
    nameB = name2;
  }
  return {
    A: nameA,
    B: nameB,
    title: resolveTitleName(rivalry.titleKey || rivalry.title) || rivalry.title || null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const STORYLINES = [

  // ══════════════════════════════════════════════════════════════════════════
  // SOLO - UNISEX (12)
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'title-chaser',
    name: 'Title Chaser',
    icon: '🏆',
    category: 'solo',
    gender: 'unisex',
    description: '{B} sets their sights on {A}\'s championship.',
    variables: ['A', 'B', 'title'],
    requiresTitle: true,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{B} makes their ambitions clear  they\'re coming for {A} and the {title}.',
          'After an impressive run, {B} calls out {A}, demanding a shot at the {title}.',
          '{B} puts {A} on notice. The {title} is in their crosshairs.',
          '{B} declares they won\'t rest until the {title} is around their waist.',
          '{A} hears the challenge from {B} loud and clear  and doesn\'t look worried. Yet.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{B} backs their words up with results. {A} is starting to take notice.',
          'Week after week, {B} inches closer to {A}. The {title} picture is shifting.',
          '{A} tries to dismiss {B}, but the numbers don\'t lie  this rivalry is real.',
          'A confrontation backstage between {A} and {B} sends a clear message: this is personal now.',
          '{B} earns their title shot after a dominant performance. {A} has nowhere to hide.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{A} and {B} collide, but neither can put the other away. The tension is at breaking point.',
          '{B} came within inches of the {title}. Now they\'re furious  and more dangerous than ever.',
          'The rivalry turns physical. {A} sends a brutal message, but {B} refuses to back down.',
          'A controversial finish between {A} and {B} leaves the {title} situation unresolved and the crowd furious.',
          '{A} keeps the {title}  for now. But {B} has made clear this is far from over.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'It all comes down to this. {A} vs {B}  one last time, with the {title} on the line.',
          'The final chapter. {B} gets one more shot at {A} and the {title}. One of them walks away changed forever.',
          'No excuses, no rematches. {A} and {B} settle their war over the {title} tonight.',
          'After everything they\'ve been through, {A} and {B} face each other one final time. The {title} goes to the rightful champion.',
          'The conclusion is here. {A} and {B} put everything on the line. This is what it\'s all been building to.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The chase is over. The rivalry between {A} and {B} has reached its end, and the {title} belongs to its rightful owner.',
          'After months of war, {A} and {B} have settled their score. The {title} picture is finally clear.',
          '{A} and {B}\'s rivalry ends tonight. One champion stands tall, the other walks away with newfound respect.',
          'The final bell rings on {A} vs {B}. The {title} saga has concluded, but the memories will last forever.',
          'It\'s done. {A} and {B} have given everything. The rivalry is over, the {title} story complete.',
        ],
      },
    ],
  },

  {
    id: 'personal-rivalry',
    name: 'Personal Rivalry',
    icon: '⚔️',
    category: 'solo',
    gender: 'unisex',
    description: '{A} and {B} make it personal.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          'A heated exchange between {A} and {B} signals the start of something personal.',
          '{A} crosses a line. {B} won\'t forget it.',
          'What started as competition between {A} and {B} has taken a darker turn.',
          '{B} steps up after being disrespected by {A}. This just got personal.',
          'Words are exchanged. Bridges are burned. {A} and {B} are now enemies.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'The bad blood between {A} and {B} spills over week after week.',
          '{B} interferes in {A}\'s match  sending a message that can\'t be ignored.',
          '{A} retaliates. {B} escalates. This rivalry shows no signs of cooling down.',
          'The locker room is split. Everyone\'s picking sides between {A} and {B}.',
          'A brutal backstage confrontation between {A} and {B} raises the stakes considerably.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'The gloves are off. {A} and {B} have stopped caring about rules.',
          'A vicious attack by {A} on {B} draws a line in the sand. Retribution is coming.',
          '{B} hits {A} where it hurts most. This has gone beyond wrestling.',
          'The entire arena is on edge every time {A} and {B} are in the same building.',
          'Management is struggling to keep {A} and {B} apart. It\'s only delaying the inevitable.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'Tonight, {A} and {B} settle their personal war once and for all.',
          'No titles. No rules. Just {A} and {B}  and months of hatred.',
          'The bad blood ends tonight. One of them walks away; the other picks up the pieces.',
          'This is the match nobody thought could contain them. {A} vs {B}. Final encounter.',
          'Everything {A} and {B} have put each other through leads to this moment.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'After all the pain and intensity, {A} and {B} have finally settled their personal war.',
          'The bad blood between {A} and {B} ends here. Both walk away forever changed.',
          '{A} and {B} fought like their lives depended on it. Now, at last, it\'s over.',
          'The personal vendetta between {A} and {B} reaches its conclusion. Respect, hard-earned.',
          'Years from now, they\'ll remember the war between {A} and {B}. Tonight, that war ends.',
        ],
      },
    ],
  },

  {
    id: 'brutal-betrayal',
    name: 'Brutal Betrayal',
    icon: '🔪',
    category: 'solo',
    gender: 'unisex',
    description: '{B} turns on {A} in the most brutal fashion.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          'Nobody saw it coming. {B} attacks {A} from behind  a blindside that shocks the entire roster.',
          '{B} turns on {A} in front of everyone. The betrayal is brutal, calculated, and cold.',
          'Allies one day, enemies the next. {B}\'s attack on {A} redefines the word "ruthless."',
          '{A} extended trust to {B}. {B} repaid it with a vicious assault nobody will forget.',
          'The crowd is stunned. {B} has just turned their back on {A}  and there\'s blood on their hands.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{A} is searching for answers. {B} isn\'t giving any  just more pain.',
          'The betrayal wasn\'t random. Slowly, {B}\'s motives are coming to light.',
          '{A} wants revenge. {B} wants to prove they never needed {A} to begin with.',
          'A tense confrontation forces {B} to finally explain their actions. The reason only makes {A} angrier.',
          'Everyone thought this would blow over. Then {B} made it clear  this was just the beginning.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{A} fights back  and {B} discovers they created a monster with that betrayal.',
          'The match between {A} and {B} turns into a war. Betrayal fuels {A}\'s every move.',
          '{B} shows no remorse. If anything, they double down  making it clear they\'d do it again.',
          'The crowd rallies behind {A}. {B} doesn\'t care  they\'re past needing approval.',
          'A brutal match ends inconclusively. But {A} sends a clear message: this isn\'t over.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'Betrayal meets reckoning. {A} and {B} settle the score tonight.',
          '{A} has waited for this moment since the night {B} turned. It\'s finally here.',
          'The story of betrayal and revenge between {A} and {B} writes its final chapter.',
          'One match. One chance for {A} to make {B} pay for everything they\'ve done.',
          'No more games. No more mind tricks. {A} and {B} end this the only way wrestlers know how.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The betrayal has been answered. {A} and {B}\'s story ends in finality.',
          'After the trust was shattered, {A} and {B} have written the final chapter in blood and sweat.',
          'The wounds run deep, but the rivalry between {A} and {B} is over. Closure, at last.',
          '{B} betrayed {A}, and {A} never forgot. Tonight, that debt is settled for good.',
          'The betrayal that started it all is now a closed book. {A} and {B} can finally move on.',
        ],
      },
    ],
  },

  {
    id: 'contested-contender',
    name: 'Contested Contender',
    icon: '⚖️',
    category: 'solo',
    gender: 'unisex',
    description: '{A} and {B} both claim they\'re the rightful number one contender.',
    variables: ['A', 'B', 'title'],
    requiresTitle: true,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          'Both {A} and {B} believe they\'ve earned the right to challenge for the {title}. Management has a problem.',
          '{A} thought the {title} shot was theirs. Then {B} made the same claim. Now it\'s a conflict.',
          'A booking decision leaves both {A} and {B} furious  they both want the {title} opportunity.',
          'The path to the {title} runs through one person: either {A} or {B}. They\'ll have to prove who deserves it.',
          'Two legitimate claims. One {title} shot. {A} and {B} refuse to stand down.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{A} and {B} keep getting in each other\'s way. Every match becomes a referendum on who\'s better.',
          'Management books a match to settle it. Neither {A} nor {B} accepts the result.',
          'The {title} shot hangs in the balance as {A} and {B} go back and forth  trading wins, trading words.',
          'A war of promos between {A} and {B} captivates the audience. Everyone has an opinion.',
          'Other contenders stay out of the way. {A} and {B} have made it clear this is their battlefield.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'What started as a debate over opportunity has turned into genuine hatred between {A} and {B}.',
          'A chaotic sequence costs both {A} and {B} in different ways. The {title} picture is murkier than ever.',
          '{A} accuses {B} of sabotage. {B} doesn\'t deny it. The rivalry takes a dirty turn.',
          'The crowd is divided. Half chants for {A}, half for {B}. Even they can\'t agree on who\'s right.',
          'Both {A} and {B} have crossed lines they can\'t uncross. This is no longer just about the {title}.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The dispute ends tonight. {A} vs {B}  winner gets the {title} shot and the right to say "I told you so."',
          'After all the arguments, all the brawls, all the controversy  {A} and {B} settle it in the ring.',
          'One match determines everything. {A} and {B} know only one of them walks out with their head held high.',
          'The ultimate tie-breaker. {A} or {B}? Tonight, the {title} match answers it once and for all.',
          'It comes down to this. {A} and {B}, one final time. The winner writes history.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The debate is settled. Between {A} and {B}, one has proven themselves the true contender.',
          'After endless arguments, {A} and {B} let their performance do the talking. The rivalry ends here.',
          'The question of who deserves it more  {A} or {B}  has finally been answered.',
          '{A} and {B}fought to prove they were the rightful challenger. Now we know.',
          'The contested spot is no longer contested. {A} and {B}\'s rivalry has reached its verdict.',
        ],
      },
    ],
  },

  {
    id: 'rookie-rise',
    name: 'Rookie\'s Rise',
    icon: '🌱',
    category: 'solo',
    gender: 'unisex',
    description: '{B}, an up-and-comer, challenges veteran {A}.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{B} is making waves  and they\'ve chosen to call out {A} as their measuring stick.',
          'The veteran {A} gets put on notice by rising star {B}. Most dismiss it. {A} doesn\'t look amused.',
          '{B} doesn\'t care about {A}\'s experience or reputation. They\'re here to make a statement.',
          'Everyone\'s talking about {B}. {A}\'s unimpressed  and happy to prove why the kid isn\'t ready.',
          '{B} challenges {A} publicly. The crowd is intrigued. {A} looks almost annoyed.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{B} shows they belong. {A} is no longer laughing.',
          'A closer-than-expected showing from {B} against {A} has the locker room buzzing.',
          '{A} tries to shut {B} down  but {B} keeps getting back up. Every single time.',
          'The veteran and the upstart trade words and victories. Neither will give an inch.',
          '{B} earns more respect with every performance. {A} is watching closer now.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{A} decides it\'s time to teach {B} a lesson the hard way. It backfires.',
          '{B} goes toe-to-toe with {A} and doesn\'t flinch. Something has shifted.',
          'The rookie label no longer fits {B}. {A} is dealing with someone genuinely dangerous.',
          '{A} and {B} have a match that steals the show. No one is dismissing {B} anymore.',
          '{A} pulls out every trick. {B} has an answer for all of them. This rivalry just leveled up.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'This is {B}\'s moment of truth. Can they beat {A} when it matters most?',
          'The final test. {B} has come so far  but {A} represents the ultimate challenge.',
          'A generation clash reaches its peak. {A} vs {B} one last time  legacy vs future.',
          'Everyone wants to see if {B} can close the deal against {A}. Tonight, we find out.',
          '{A} or {B}? Experience or hunger? One match decides which matters more.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The rookie proved they belong. {A} vs {B}  a rivalry that defined a career, now complete.',
          '{B} arrived with something to prove against {A}. Tonight, they\'ve proven everything.',
          'From underdog to equal, {B}\'s journey against {A} ends with heads held high.',
          '{A} tested {B} like no one else could. The rivalry that shaped a star is over.',
          'The legend and the rising star  {A} and {B}  close this chapter with mutual respect.',
        ],
      },
    ],
  },

  {
    id: 'domination-rematch',
    name: 'Rematch After Domination',
    icon: '🔁',
    category: 'solo',
    gender: 'unisex',
    description: '{B} seeks revenge after being dominated by {A}.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} made {B} look powerless. Now {B} wants to prove that was a fluke.',
          'The humiliation at the hands of {A} has lit a fire in {B} that nothing can extinguish.',
          '{B} returns with a score to settle. {A} dominated them once  {B} intends to make sure it never happens again.',
          'Nobody forgets the way {A} dismantled {B}. Least of all {B} themselves.',
          '{B} has been rebuilding since {A} took them apart. Now they\'re back  and they want answers.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{B} shows they\'ve evolved. {A} starts to wonder if they underestimated them.',
          'A string of strong performances from {B} demands {A}\'s attention. Reluctantly given.',
          '{A} still holds the psychological edge  for now. {B} is chipping away at it.',
          'The rematch takes shape. {B} is different. More focused. More dangerous.',
          '{A} taunts {B} with the memory of their last encounter. {B} uses it as fuel.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{B} pushes {A} further than anyone expected. The dynamic is shifting.',
          'A heated exchange leaves both {A} and {B} rattled. {B} is no longer a pushover.',
          '{A} realizes they need to take this seriously. {B} isn\'t the same person they beat before.',
          'The match between {A} and {B} ends in controversy  guaranteeing another encounter.',
          '{B} comes this close to getting revenge. {A} survives, but only barely.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The rematch is finally here. {B} has waited for this moment. {A} refuses to be beaten.',
          'Everything changes tonight  or everything stays the same. {A} vs {B}, final chapter.',
          'Can {B} rewrite the ending of their story against {A}? This is their last chance.',
          'Revenge or repeat? {A} and {B} settle it in the ring, once and for all.',
          'The slate gets wiped clean tonight. {B} has earned this shot at {A}. Time to see if they can finish it.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The rematch is over. {A} and {B}\'s story of dominance and revenge has concluded.',
          'After being dominated, {B} clawed back. The rivalry with {A} ends on their terms.',
          '{A} ruled once. {B} fought back. Now, their war of wills is finished.',
          'Domination, revenge, redemption  {A} vs {B} had it all. Tonight, it ends.',
          'The power dynamic between {A} and {B} has shifted for the last time. The rivalry is over.',
        ],
      },
    ],
  },

  {
    id: 'war-of-words',
    name: 'War of Words',
    icon: '🎤',
    category: 'solo',
    gender: 'unisex',
    description: '{A} and {B} wage a promo war that eventually turns physical.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} has something to say about {B}. {B} is ready to respond. Let the mic wars begin.',
          'The rivalry starts with words  sharp, cutting words that cut deeper than any move.',
          '{B} calls {A} out on the microphone. The crowd is hooked immediately.',
          'A war of promos breaks out between {A} and {B}. Nobody\'s sure when fists will replace words.',
          '{A} opens their mouth. {B} opens theirs. The arena hasn\'t heard verbal warfare like this in years.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'Week after week, {A} and {B} trade barbs. The audience can\'t get enough.',
          'The verbal battle escalates. {A} and {B} are getting closer to snapping.',
          'A particularly vicious line from {B} leaves {A} visibly unsettled. Words are leaving marks.',
          '{A} says something unforgivable. {B} looks like they\'re about to make it physical right now.',
          'The war of words is breaking down into something more dangerous. Both {A} and {B} are on edge.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'The microphone is no longer enough. {A} and {B} come to blows.',
          'A promo segment implodes into a brawl. Words have been replaced by fists.',
          '{B} says enough. They\'re done talking. {A} finds out quickly what that means.',
          'The crowd has watched this build for weeks. Now it\'s explosive  and nobody is safe in the vicinity.',
          'Security can barely keep {A} and {B} apart. The match can\'t come soon enough.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'Tonight, {A} and {B} stop talking and start fighting  for real, and for the last time.',
          'Every word. Every insult. Every taunt. It all comes down to this match between {A} and {B}.',
          'The war of words ends in the ring. Only one of them gets to claim the last word.',
          '{A} and {B} have said everything there is to say. Now they let their actions do the talking.',
          'The final confrontation. After all the promos, all the verbal jousting  {A} vs {B} in the ring.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The talking is done. {A} and {B} let their actions write the final chapter.',
          'After all the verbal sparring, {A} and {B} settled it where it counts. The rivalry concludes.',
          'Words turned to war. Now, the war between {A} and {B} is over.',
          '{A} and {B} traded barbs for months. Tonight, they traded respect. It\'s finished.',
          'The mic wars between {A} and {B} culminated here. No more words needed.',
        ],
      },
    ],
  },

  {
    id: 'broken-respect',
    name: 'Broken Respect',
    icon: '🤝',
    category: 'solo',
    gender: 'unisex',
    description: 'A rivalry born from mutual respect that turns into bitter conflict.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} and {B} have always respected each other. That\'s what makes the first crack so painful.',
          'A rivalry that starts with a handshake  but something shifts, and neither can ignore it.',
          'The respect between {A} and {B} is real. So is the tension now building beneath it.',
          '{A} didn\'t mean for it to go this way. Neither did {B}. But here they are.',
          'Competitors who once admired each other now find themselves on opposite sides.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'Competition has replaced camaraderie. {A} and {B} are bringing out the worst in each other.',
          'What started as friendly competition has an edge to it now. {A} and {B} both feel it.',
          'A moment of frustration between {A} and {B} leaves a scar that won\'t heal cleanly.',
          'The respect is fading fast. In its place: pride, ego, and a need to prove something.',
          '{A} and {B} fight each other like they\'re trying to forget what they once were to each other.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'Any pretense of respect is gone. {A} and {B} are fully at war now.',
          'The most painful rivalries are the ones between people who used to respect each other. This one qualifies.',
          '{A} crosses a line that {B} never expected from them. The respect is officially dead.',
          '{B} says things about {A} that cannot be taken back. The relationship is destroyed.',
          'The crowd watches with sadness and excitement. Two people they love, tearing each other apart.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'Former rivals who once respected each other now fight to prove they never needed that respect.',
          'The final match. Whatever {A} and {B} once were to each other, this ends it.',
          'Out of the ashes of respect comes one last brutal confrontation between {A} and {B}.',
          'Tonight determines who was right all along  {A} or {B}. Respect has nothing to do with it now.',
          'One final encounter between {A} and {B}  a fitting end to a rivalry that began with admiration.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The respect that was shattered has been tested one final time. {A} and {B}\'s story ends here.',
          'Once allies, then enemies  {A} and {B} have written their final chapter.',
          'Broken bonds don\'t easily mend. But {A} and {B}\'s rivalry has reached its end.',
          '{A} and {B} lost respect for each other long ago. Tonight, they found closure.',
          'From friendship to fury, {A} and {B}\'s journey concludes with hard-won understanding.',
        ],
      },
    ],
  },

  {
    id: 'mind-games',
    name: 'Mind Games',
    icon: '🧠',
    category: 'solo',
    gender: 'unisex',
    description: '{A} and {B} wage psychological warfare.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} isn\'t just trying to beat {B}  they\'re trying to get inside their head.',
          'The mind games begin. {B} isn\'t sure what {A} is planning, and that\'s exactly the point.',
          '{A} makes a calculated move designed to unsettle {B}. It works.',
          'This rivalry isn\'t going to be about strength. {A} wants to win before the bell even rings.',
          '{B} senses something is off. {A} is playing a game  and {B} doesn\'t know the rules yet.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{A}\'s tactics are working. {B} is visibly rattled and making mistakes.',
          'The psychological pressure from {A} is relentless. {B} is struggling to focus.',
          '{B} tries to fight back mentally  but {A} stays one step ahead.',
          'What {A} is doing to {B}\'s confidence is almost as damaging as any finisher.',
          'Everyone can see the effect {A}\'s mind games are having on {B}. This is masterful  and cruel.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{B} snaps. And when they do, {A} realizes they may have pushed too far.',
          'The mind games backfire  a cornered {B} is more dangerous than a comfortable one.',
          '{B} figures out {A}\'s pattern and turns the psychological tables. Suddenly, {A} looks unsure.',
          'The psychological warfare is now mutual. {A} and {B} are both in each other\'s heads.',
          'A match between {A} and {B} becomes as much a battle of wills as a physical contest.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The mind games are over. {A} and {B} settle this the old-fashioned way  in the ring.',
          'After weeks of psychological warfare, it all comes down to one match between {A} and {B}.',
          'No more tricks. No more tactics. Just {A} and {B}  and whoever is mentally stronger.',
          'The war inside both their heads ends tonight. One match. One winner. One broken.',
          'Tonight {A} and {B} find out who the stronger mind belongs to.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The psychological warfare between {A} and {B} has run its course. No more games.',
          'The mind games end tonight. {A} and {B} finally put this twisted rivalry to rest.',
          'After manipulating and outsmarting each other, {A} and {B} reach the endgame.',
          '{A} and {B} played each other like chess pieces. Now, the board is clear.',
          'The mental battle between {A} and {B} was exhausting. It\'s finally over.',
        ],
      },
    ],
  },

  {
    id: 'injustice',
    name: 'Injustice to Repair',
    icon: '⚖️',
    category: 'solo',
    gender: 'unisex',
    description: '{B} was wronged by {A} and seeks to set things right.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} did something to {B} that nobody can justify. Now {B} is asking for accountability.',
          'The injustice is clear for everyone to see  {A} wronged {B}, and there\'s been no consequence.',
          '{B} didn\'t start this. {A} did. And {B} isn\'t going to let it go unanswered.',
          'What happened to {B} at the hands of {A} was wrong. {B} is here to make it right.',
          '{A} thought {B} would just move on. {B} is here to prove they were very wrong about that.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{B} fights back  not just physically, but for their dignity and their name.',
          'The crowd rallies behind {B}. What {A} did was wrong, and everyone knows it.',
          '{A} refuses to acknowledge wrongdoing. That only makes {B} more determined.',
          'Every match {B} wins feels like justice being slowly served. {A} is running out of excuses.',
          '{B} is on a mission. {A} has never faced someone fighting with this much righteous anger.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{A} doubles down, making the injustice even worse. The crowd is furious.',
          'A brutal match between {A} and {B} ends without closure. The injustice festers.',
          '{B} gets so close to justice  only for {A} to find a way to escape. Again.',
          'The anger and pain fuel {B}\'s every move. {A} is dealing with someone on a different level.',
          'Everyone watching wants to see {B} get the justice they deserve. The match delivers tension  but not resolution. Yet.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'Tonight, {B} gets the chance to right what was wrong. One match. One moment of truth.',
          'The scales of justice tip tonight. {A} faces the full consequences of what they did to {B}.',
          '{B} has fought for this moment. The injustice ends here  one way or another.',
          'This is what {B} has been building toward. A chance to face {A} and prove that wrongs get righted.',
          'The final reckoning. {A} and {B} settle a score that\'s been burning for weeks.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'Justice, delayed but delivered. {A} vs {B}  a rivalry born of wrong, ended with resolution.',
          'The injustice that sparked this war has been addressed. {A} and {B} can finally move forward.',
          '{B} was wronged by {A}, and fought to make it right. Tonight, that fight ends.',
          'Fairness was always the question. {A} and {B}\'s rivalry has answered it for good.',
          'From injustice to closure  {A} and {B}\'s story concludes with balance restored.',
        ],
      },
    ],
  },

  {
    id: 'untouchable-champion',
    name: 'Untouchable Champion',
    icon: '👑',
    category: 'solo',
    gender: 'unisex',
    description: '{A} is an arrogant champion who believes no one can touch them. {B} disagrees.',
    variables: ['A', 'B', 'title'],
    requiresTitle: true,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} believes no one on the roster is worthy of the {title}. {B} steps up to prove them wrong.',
          'The {title} has made {A} untouchable  or so they think. {B} has a different opinion.',
          '{A} dismisses every challenger. {B} refuses to be dismissed.',
          'Arrogance personified. {A} holds the {title} like it\'s their birthright. {B} is about to challenge that narrative.',
          '{B} watches {A} parade around with the {title} and decides: enough.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{A} underestimates {B}. {B} is perfectly content to let them.',
          'The champion {A} tries to make an example out of {B}. It doesn\'t go as planned.',
          '{B} continues to climb despite {A}\'s attempts to shut them down.',
          '{A}\'s confidence in their {title} reign is starting to look like overconfidence.',
          '{B} earns their shot. {A} is still not concerned. They should be.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{A} finally takes {B} seriously  and the realization visibly shakes them.',
          'The invincibility aura around {A} cracks in a match against {B}. The {title} picture just changed.',
          '{A} survives {B}\'s challenge  but doesn\'t look untouchable anymore.',
          '{B} proves in front of everyone that {A} can be hurt. The dynasty looks mortal.',
          'A near-fall on {A} sends shockwaves. {B} nearly did the unthinkable. Nearly.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'Can {B} slay the giant? {A}\'s reign faces its toughest test tonight.',
          'The era of {A}\'s dominance gets challenged in the most direct way possible. {B} is here to end it.',
          'One match to determine if {A}\'s reign continues  or if {B} becomes the one who toppled them.',
          'The {title} and the legacy of dominance are on the line. {A} vs {B}, one final time.',
          'Tonight: dynasty vs destiny. {A} and {B} write the next chapter of {title} history.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The untouchable aura is tested for the last time. {A} vs {B}  rivalry complete.',
          '{A} seemed invincible. {B} pushed them to the limit. Their war is over.',
          'The unstoppable force met the immovable object. {A} and {B}\'s clash has concluded.',
          '{B} challenged {A}\'s reign like no one else. Tonight, that challenge ends.',
          'The myth of {A}\'s invincibility was tested by {B}. Now, the story is written.',
        ],
      },
    ],
  },

  {
    id: 'win-obsession',
    name: 'Win Obsession',
    icon: '🎯',
    category: 'solo',
    gender: 'unisex',
    description: '{B} becomes obsessed with getting a win over {A}.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{B} has faced {A} before  and the result still haunts them. They\'re back for a different outcome.',
          'It starts simply enough: {B} wants to beat {A}. But the want quickly becomes need.',
          '{A} has {B}\'s number. {B} is determined to change that.',
          'The obsession with beating {A} has taken hold of {B}. Everything else is secondary.',
          '{B} makes it their singular focus: getting a definitive win over {A}.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{B} comes close  but {A} finds a way. The obsession deepens.',
          'The more {B} chases the win over {A}, the more it consumes them.',
          '{A} knows what {B} wants. And knowing it gives {A} an edge.',
          '{B}\'s obsession is becoming visible to everyone  including {A}, who is exploiting it.',
          'Another loss to {A} sends {B} spiraling. But also sharpens their focus to a razor edge.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{B} is getting dangerously close. {A} can feel the pressure now.',
          'The obsession has refined {B}  they\'re better than they\'ve ever been. Because of {A}.',
          '{A} barely escapes with a victory. {B} has never looked more dangerous.',
          'A thrilling encounter between {A} and {B} ends agonizingly close for {B}. So close.',
          'The crowd senses it: {B} is going to get that win. It\'s just a matter of when.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'One more shot. {B} gets one more chance to beat {A}. The obsession reaches its conclusion.',
          'This is the match {B} has been building toward. Beat {A}  or walk away empty.',
          '{A} and {B} meet one final time. {B} doesn\'t care about anything except getting that win.',
          'The obsession ends tonight  one way or another. {A} vs {B}, final encounter.',
          'Months of pursuit lead to this. {B} has earned the chance to finally settle the score with {A}.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The obsession with victory consumed them both. {A} and {B}\'s rivalry ends here.',
          'Winning was everything to {A} and {B}. Now, they\'ve both won something more  closure.',
          '{A} and {B} chased victory at any cost. Tonight, they pay the final price.',
          'The win-at-all-costs mentality defined {A} vs {B}. That chapter closes now.',
          'Obsessed with beating each other, {A} and {B} finally find the finish line.',
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // SOLO  MIXED (3)  gender: 'mixed' (one male, one female)
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'ego-clash',
    name: 'Ego Clash',
    icon: '💥',
    category: 'solo',
    gender: 'mixed',
    description: '{A} and {B} let their egos drive them into conflict.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          'Two massive egos. One roster. {A} and {B} were always going to collide.',
          '{A} says something. {B} takes offense. Two egos are now officially at war.',
          'Neither {A} nor {B} is used to sharing the spotlight. This rivalry was inevitable.',
          'The tension between {A} and {B} has been building for weeks. The ego match begins now.',
          '{B} refuses to be overshadowed by {A}. {A} refuses to acknowledge {B}\'s right to challenge them.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'The ego battle between {A} and {B} spills into every segment, every match, every promo.',
          'Two people convinced they\'re the most important person in the building  and only one can be right.',
          '{A} and {B} keep one-upping each other. The audience is thoroughly entertained.',
          'Neither will back down. Neither will admit the other is better. The clash intensifies.',
          'Management is exhausted trying to manage the egos of both {A} and {B}.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'The ego war turns personal. {A} and {B} have both said things they can\'t take back.',
          'A spectacular confrontation makes it clear: one ego must be broken for this to end.',
          '{A} humiliates {B} in front of everyone. {B} vows to return the favor tenfold.',
          'What started as ego has become genuine animosity between {A} and {B}.',
          'The crowd can\'t look away. Two massive personalities in open warfare.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'One match. One winner. One ego crushed. {A} vs {B}, final confrontation.',
          'Tonight, the biggest ego in the building walks out victorious. {A} or {B}?',
          'The ego clash reaches its conclusion. {A} and {B} leave it all in the ring.',
          'After all the bravado, all the posturing  {A} and {B} settle it the only way that matters.',
          'One of them walks away humbled. The other walks away vindicated. {A} vs {B}, tonight.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'Two massive egos collided. {A} and {B}\'s rivalry ends with one humbled, one vindicated.',
          'The battle of pride between {A} and {B} reaches its conclusion. Egos checked.',
          '{A} and {B} refused to back down. Tonight, their war of egos is over.',
          'Pride drove them to destroy each other. {A} vs {B}  the rivalry ends here.',
          'The clash of titans  {A} and {B}  has run its course. Respect earned, rivalry over.',
        ],
      },
    ],
  },

  {
    id: 'cross-manipulation',
    name: 'Cross Manipulation',
    icon: '🕸️',
    category: 'solo',
    gender: 'mixed',
    description: '{A} and {B} try to manipulate and outmaneuver each other.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} thinks they can use {B} for their own ends. {B} has the exact same idea.',
          'What looks like an alliance between {A} and {B} is anything but  both are playing angles.',
          'The manipulation starts subtly. {A} and {B} smile at each other while sharpening their knives.',
          'Everyone can see it except {A} and {B}: they\'re both being played by the other.',
          'A careful dance of deception begins. {A} and {B} each believe they\'re the puppet master.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'The layers of deception between {A} and {B} multiply. Trust is nonexistent.',
          '{A} makes a move. {B} counters. Neither knows how deep this rabbit hole goes.',
          'Whispers and schemes fill the backstage area. {A} and {B} are playing chess with each other.',
          'An unexpected move from {B} forces {A} to reveal more of their hand than intended.',
          'The audience watches the manipulation unfold in real time. This is must-see television.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'The masks come off. {A} and {B} stop pretending and acknowledge what this really is.',
          'A spectacular betrayal  or attempted one  blows the whole game wide open.',
          'The manipulation spins out of control. {A} and {B} are caught in a web of their own making.',
          'Raw anger replaces calculated moves. {A} and {B} are done playing games.',
          'Everything unravels in one chaotic segment. The real rivalry  without masks  can finally begin.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'No more manipulation. {A} and {B} settle this honestly  in the ring.',
          'The final gambit. {A} and {B} each have one last trick to play. Then it\'s over.',
          'After all the schemes and counter-schemes, the truth comes out in the ring between {A} and {B}.',
          'The most calculated rivalry in recent memory ends tonight  and even now, nobody\'s sure who will blink first.',
          'The game is over. {A} and {B} face each other with nothing left to hide.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The manipulation, the schemes  {A} and {B}\'s twisted game ends tonight.',
          'They used each other, betrayed each other. {A} and {B}\'s rivalry concludes with truth.',
          '{A} and {B} played dangerous games. Now, the game is over.',
          'Manipulation gave way to confrontation. {A} vs {B}  rivalry resolved.',
          'The lies between {A} and {B} are laid bare. Their story ends here.',
        ],
      },
    ],
  },

  {
    id: 'no-limits',
    name: 'Competition Without Limits',
    icon: '🔥',
    category: 'solo',
    gender: 'mixed',
    description: '{A} and {B} push every boundary in their pursuit of supremacy.',
    variables: ['A', 'B'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{A} and {B} don\'t recognize any limits  not in competition, and certainly not in this rivalry.',
          'A rivalry with no guardrails. {A} and {B} are both willing to go further than anyone else.',
          'The gloves are off from day one. {A} and {B} operate without a rulebook.',
          'What makes this dangerous: {A} and {B} both genuinely have no limits. This is going to escalate fast.',
          'Nobody knows where this ends because {A} and {B} don\'t know where it ends either.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'Each week, {A} and {B} raise the stakes higher. Others on the roster keep their distance.',
          'The no-limits approach is causing chaos  and neither {A} nor {B} is apologizing.',
          'Management tries to impose boundaries. {A} and {B} ignore them both.',
          'Every encounter between {A} and {B} leaves destruction in its wake.',
          'The rivalry goes to places no one expected. And it\'s only going to go further.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'Things have gone too far  even for a rivalry that operates without limits.',
          '{A} and {B} are hurting each other in ways that go beyond competition.',
          'The roster watches in equal parts horror and fascination as {A} and {B} escalate again.',
          'A match with no rules between {A} and {B} lives up to every expectation  and breaks every one.',
          'The no-limits rivalry has reached a point where everyone\'s asking: when does it stop?',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The final limit. {A} and {B} have one last encounter  and this time, something has to give.',
          'A rivalry with no rules needs a definitive ending. Tonight, {A} and {B} provide one.',
          'No boundaries, no rules, no mercy. The conclusion of {A} vs {B}.',
          'The most intense rivalry ends the way it always had to  with everything on the line.',
          'After everything they\'ve put each other through, {A} and {B} have one last battle. No limits. No mercy.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'They pushed every boundary. {A} and {B}\'s extreme rivalry has reached its limit.',
          'No rules, no mercy  {A} and {B} took it as far as it could go. Now it\'s done.',
          '{A} and {B} broke every rule in the book. Tonight, the book closes.',
          'The most brutal rivalry  {A} vs {B}  ends with scars that will never heal.',
          'They held nothing back. {A} and {B}\'s war of extremes is finally over.',
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // TAG TEAM (5)
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'team-implosion',
    name: 'Team Implosion',
    icon: '💣',
    category: 'tag',
    gender: 'unisex',
    description: '{teamA} ({A1} & {A2}) implodes from within, leading to a rivalry against {teamB}.',
    variables: ['A1', 'A2', 'B1', 'B2', 'teamA', 'teamB'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          'Cracks appear in {teamA}. {A1} and {A2} aren\'t seeing eye to eye  and {teamB} is watching closely.',
          '{teamA}\'s unity is fracturing. {teamB} smells blood and moves in.',
          'What was once a dominant team is showing fault lines. {A1} and {A2} are pulling in different directions.',
          '{teamB} doesn\'t just want to beat {teamA}  they want to be the ones who expose that {teamA} is finished.',
          'The internal tension within {teamA} is becoming public. {teamB} couldn\'t have planned this better.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'A crucial miscommunication between {A1} and {A2} costs {teamA} dearly. {teamB} capitalizes.',
          'The cracks within {teamA} widen. {B1} and {B2} keep applying the pressure.',
          '{teamB} has figured out the formula: exploit the tension between {A1} and {A2}.',
          '{A1} blames {A2}. {A2} returns the favor. Meanwhile, {teamB} thrives.',
          'The implosion of {teamA} plays out in public  and {teamB} has a front row seat.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{teamA} is barely holding together. One more push from {teamB} could break them entirely.',
          'An explosive backstage incident between {A1} and {A2} brings the implosion to a head.',
          '{teamB} sees a fractured team. {teamA} sees one last chance to prove they can still function.',
          'The tag match becomes a microcosm of {teamA}\'s struggles  and {teamB}\'s strength in contrast.',
          'The question isn\'t just can {teamA} beat {teamB}. It\'s can {A1} and {A2} coexist long enough to try.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The final test for {teamA}. Survive {teamB}  or fall apart completely.',
          '{teamA} vs {teamB} one last time. Unity versus dysfunction. Something has to give.',
          'The implosion either gets contained or it destroys {teamA} tonight. {teamB} is ready either way.',
          '{A1} and {A2} must put aside everything to face {B1} and {B2}. Whether they can is another story.',
          'The conclusion of the {teamA} saga. Whatever happens against {teamB} tonight changes everything.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The team is dead. {teamA} and {teamB}  former allies, now concluded enemies.',
          'What once was unity is now divided forever. The rivalry between {teamA} and {teamB} ends here.',
          '{teamA} and {teamB} tore their partnership apart. Tonight, they bury what\'s left.',
          'The implosion is complete. {teamA} vs {teamB}  a rivalry born of betrayal, ended with finality.',
          'From brothers to enemies, {teamA} and {teamB} close this painful chapter.',
        ],
      },
    ],
  },

  {
    id: 'tag-title-chase',
    name: 'Tag Title Chase',
    icon: '🏅',
    category: 'tag',
    gender: 'unisex',
    description: '{teamB} challenges {teamA} for tag team supremacy.',
    variables: ['A1', 'A2', 'B1', 'B2', 'teamA', 'teamB', 'title'],
    requiresTitle: true,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{teamB} make their challenge official  they\'re coming for {teamA} and the {title}.',
          '{B1} and {B2} step up to challenge {A1} and {A2}. The {title} hunt begins.',
          '{teamA} has held the {title} long enough. {teamB} intends to change that narrative.',
          'A dominant performance earns {teamB} the right to challenge {teamA}. The {title} is on notice.',
          '{B1} and {B2} look each other in the eye and make a promise: the {title} comes home with them.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{teamB} keeps proving they belong. {teamA} is taking the challenge more seriously now.',
          'The rivalry between {teamA} and {teamB} heats up  tag team warfare at its finest.',
          'A non-title encounter between the two teams ends inconclusively. The {title} match feels inevitable.',
          '{A1} and {A2} know what {B1} and {B2} are capable of. That\'s why they\'re preparing.',
          'The {title} match draws closer. Both teams are putting in work  and putting each other through the wringer.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'A brutal match between {teamA} and {teamB} raises the stakes  and the tension  exponentially.',
          '{teamA} survives a scare from {teamB}. But only barely. The champions look vulnerable.',
          '{teamB} came this close to the {title}. Now they\'re angrier and more dangerous.',
          'The rivalry spills outside the ring. {teamA} and {teamB} are going to war.',
          'A controversial finish between {teamA} and {teamB} guarantees one more chapter  the biggest one.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The {title} is on the line one final time. {teamA} vs {teamB}  everything decided tonight.',
          'One match. The {title}. {A1} & {A2} vs {B1} & {B2}. The conclusion of tag team warfare.',
          'Championship match. No more build-up. {teamA} and {teamB} settle it.',
          'After everything they\'ve been through, {teamA} and {teamB} have their final encounter over the {title}.',
          'The {title} changes hands or the dynasty continues. {teamA} vs {teamB}, final chapter.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The chase for tag team gold ends tonight. {teamA} vs {teamB}  rivalry concluded.',
          '{teamA} and {teamB} fought for the titles with everything they had. Now, it\'s over.',
          'The tag team championship picture is finally clear. {teamA} and {teamB}\'s war is done.',
          'Months of chasing, fighting, earning  {teamA} and {teamB} reach the end of their journey.',
          'The tag team rivalry between {teamA} and {teamB} has run its course. Respect earned.',
        ],
      },
    ],
  },

  {
    id: 'dominant-reign',
    name: 'Dominant Reign',
    icon: '💪',
    category: 'tag',
    gender: 'unisex',
    description: '{teamA} is an unbeatable force. {teamB} rises to challenge them.',
    variables: ['A1', 'A2', 'B1', 'B2', 'teamA', 'teamB'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{teamA} has been untouchable. {B1} and {B2} don\'t believe in untouchable.',
          'Every team has tried and failed to stop {teamA}. {teamB} thinks they\'re different.',
          '{teamA} has run roughshod over the division. {teamB} steps up to be the ones who end it.',
          '{B1} and {B2} study {teamA} carefully. They\'re not impressed. They\'re motivated.',
          'The reign of {teamA} has been dominant. {teamB} has seen enough.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{teamB} gives {teamA} their toughest challenge yet  and survives to tell the tale.',
          'The dominance of {teamA} is being seriously tested by {B1} and {B2} for the first time.',
          '{teamA} wins  but for the first time, it wasn\'t easy. {teamB} has found their formula.',
          '{A1} and {A2} look at {teamB} with new eyes. These two are actually a threat.',
          'The dynasty of {teamA} faces its most credible challenge in {teamB}.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          '{teamA}\'s aura of invincibility takes a hit. {teamB} nearly did the impossible.',
          'A stunning near-fall by {teamB} on {teamA} sends the crowd into a frenzy.',
          '{teamA} survives  but the dynasty looks mortal for the first time. {teamB} did that.',
          'The rivalry reaches fever pitch. Can anyone stop {teamA}? {teamB} says yes.',
          'Both teams have brought out the best  and worst  in each other. One final match awaits.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The dynasty or the dynasty-breakers. {teamA} vs {teamB}, final encounter.',
          'Can {teamB} do what no team has done  beat {teamA} when it counts most?',
          'Tonight answers the question everyone has been asking: are {teamA} truly unbeatable?',
          'One final collision between {teamA} and {teamB}. The answer to everything.',
          '{A1} & {A2} vs {B1} & {B2}. Dominance challenged. Legacy defined.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'The reign of dominance ends or continues  either way, {teamA} vs {teamB} is over.',
          '{teamA} held the tag division hostage. {teamB} fought to free it. Their war concludes.',
          'Dominance challenged, dominance tested. {teamA} and {teamB}\'s rivalry ends here.',
          'The unstoppable force met worthy challengers in {teamB}. {teamA}\'s war is done.',
          '{teamA} and {teamB} defined tag team excellence. Their story ends tonight.',
        ],
      },
    ],
  },

  {
    id: 'mirror-match',
    name: 'Mirror Match',
    icon: '🪞',
    category: 'tag',
    gender: 'unisex',
    description: '{teamA} and {teamB} are eerily similar  same style, same attitude, same hunger.',
    variables: ['A1', 'A2', 'B1', 'B2', 'teamA', 'teamB'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{teamA} and {teamB} look at each other and see a reflection  and they don\'t like what they see.',
          'Two teams cut from the same cloth. {teamA} and {teamB} were always going to collide.',
          'The similarities between {teamA} and {teamB} are obvious to everyone. Including them.',
          '{B1} and {B2} are everything {A1} and {A2} are. That\'s precisely the problem.',
          'Same style. Same intensity. Same drive. {teamA} vs {teamB} was destined from the start.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          'The mirror match quality makes every encounter between {teamA} and {teamB} razor-close.',
          'Because they\'re so similar, {teamA} and {teamB} know exactly how to get under each other\'s skin.',
          '{A1} and {A2} know what {B1} and {B2} are going to do  and {B1} and {B2} know the same.',
          'The chess match between these two teams is tactically fascinating. And fiercely competitive.',
          'Neither team can pull away. Every time one gains an edge, the other matches it.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'The similarities stop being interesting and start being infuriating for both {teamA} and {teamB}.',
          'What separates {teamA} from {teamB}? They\'re about to find out the hard way.',
          'The mirror match turns brutal. These teams aren\'t just competing  they\'re trying to prove which version is better.',
          '{A1} and {A2} are sick of seeing themselves in {B1} and {B2}. Time to end the reflection.',
          'The rivalry reaches its most intense point. {teamA} and {teamB} are pushing each other to the limit.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The mirror shatters tonight. {teamA} vs {teamB}, final confrontation.',
          'One team is better. Tonight we find out which one. {teamA} vs {teamB}.',
          'The reflection ends here. {A1} & {A2} vs {B1} & {B2}  settling the debate for good.',
          'Two teams. Same DNA. One winner. The final match between {teamA} and {teamB} answers everything.',
          'After all the parallels, all the similarities  one final bout determines which team stands alone.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'Mirror images no more. {teamA} and {teamB} have proven who they really are.',
          'The teams that mirrored each other have diverged forever. {teamA} vs {teamB}  over.',
          '{teamA} and {teamB} were reflections of the same style. Now, one stands alone.',
          'Two teams, identical in skill, different in heart. {teamA} and {teamB}\'s rivalry concludes.',
          'The mirror is shattered. {teamA} and {teamB} walk separate paths from here.',
        ],
      },
    ],
  },

  {
    id: 'collective-rematch',
    name: 'Collective Rematch',
    icon: '🔄',
    category: 'tag',
    gender: 'unisex',
    description: '{teamB} seeks revenge after a defeat at the hands of {teamA}.',
    variables: ['A1', 'A2', 'B1', 'B2', 'teamA', 'teamB'],
    requiresTitle: false,
    phases: [
      {
        level: 1, name: 'Setup',
        variants: [
          '{teamB} hasn\'t forgotten what {teamA} did to them. Revenge is coming.',
          'The loss to {teamA} still burns for {B1} and {B2}. They want a rematch. They need a rematch.',
          '{teamA} thought they\'d seen the last of {teamB}. They were wrong.',
          'The unfinished business between {teamA} and {teamB} has to be settled. {teamB} demands it.',
          '{B1} and {B2} regroup, rebuild, and return  with {teamA} squarely in their sights.',
        ],
      },
      {
        level: 2, name: 'Build',
        variants: [
          '{teamB} has clearly evolved since their last encounter with {teamA}. {A1} and {A2} notice.',
          'The rematch path for {teamB} is hard-fought  but their motivation is undeniable.',
          '{teamA} expected to be done with {teamB}. {B1} and {B2} have other plans.',
          'The hunger driving {teamB} comes from the memory of their loss. It\'s a powerful fuel.',
          'Every win {teamB} picks up brings them closer to getting their hands on {teamA} again.',
        ],
      },
      {
        level: 3, name: 'Heat',
        variants: [
          'The match between {teamA} and {teamB} is even more intense than their last encounter. Revenge does that.',
          '{teamB} pushes {teamA} further than anyone else has managed. The rematch is nearly theirs.',
          'A controversial result between {teamA} and {teamB} guarantees one more chapter.',
          '{teamA} escapes again  but not comfortably. {teamB} is better, and everyone knows it.',
          'The desire for revenge has made {B1} and {B2} into the best version of {teamB}.',
        ],
      },
      {
        level: 4, name: 'Climax',
        variants: [
          'The rematch is here. {teamB} finally gets the definitive shot at {teamA} they\'ve been fighting for.',
          'Revenge or repeat? {teamA} vs {teamB}, the final match.',
          'Everything {teamB} has done to get here led to this moment against {teamA}.',
          'Can {B1} and {B2} rewrite the ending of their story against {A1} and {A2}? Tonight we find out.',
          '{teamA} or {teamB}  one team walks out vindicated. The other goes home with nothing but what-ifs.',
        ],
      },
      {
        level: 5, name: 'Conclusion',
        variants: [
          'Old wounds revisited. {teamA} and {teamB}\'s rematch rivalry has reached its end.',
          'History repeated itself between {teamA} and {teamB}. Now, the book closes for good.',
          'The rematch that had to happen is over. {teamA} and {teamB} can finally move on.',
          '{teamA} and {teamB} settled old scores. Their chapter is closed.',
          'From the past to the present, {teamA} vs {teamB}  the final word has been spoken.',
        ],
      },
    ],
  },

];

export default STORYLINES;
